import { gql } from "urql"

import { AO_MIN_INGESTED_AT, goldsky } from "./graphql-client"
import { TransactionsResponse } from "../types"
import { messageFields } from "../utils/arweave-utils"
import { supabase } from "./supabase"

const AllSwapsQuery = gql`
  query ($poolId: String!, $limit: Int!, $sortOrder: SortOrder!, $cursor: String, $traders: [String!]) {
    transactions(
      sort: $sortOrder
      first: $limit
      after: $cursor

      tags: [
        { name: "Action", values: ["Order-Confirmation"] }
        { name: "From-Process", values: [$poolId] }
        { name: "Relay-To", values: $traders }
      ]
      ${AO_MIN_INGESTED_AT}
    ) {
      ...MessageFields
    }
  }

  ${messageFields}
`

export type Swap = {
  cursor: string
  id: string
  fromToken: string
  toToken: string
  fee: string
  fromQuantity: number
  toQuantity: number
  tags: Record<string, string>
  ingestedAt: number
  from: string
  reservesA: number
  reservesB: number
  ref: number
  tokenAPrice?: number // USD price of token A
  tokenBPrice?: number // USD price of token B
  tokenA?: string // Token A identifier
  tokenB?: string // Token B identifier
}

// Type for Supabase amm_transactions table
type AmmTransaction = {
  id: string
  block_height: number | null
  block_id: string | null
  sender: string | null
  created_at_ts: number | null
  created_at_ts_ms: number | null
  to_token: string | null
  from_token: string | null
  from_quantity: number | null
  to_quantity: number | null
  fee_percentage: number | null
  lp_fee_percentage: number | null
  protocol_fee_percentage: number | null
  reserves_token_a: string | null
  reserves_token_b: string | null
  amm_process: string | null
  token_a: string | null
  token_b: string | null
  token_a_price: number | null
  token_b_price: number | null
  user_id: string | null
  order_id: string | null
  total_fee: number | null
  topic: string | null
  token_a_usd_price: number | null
  token_b_usd_price: number | null
  reference_value: string | null
}

// New Supabase-based implementation
export async function getAllSwaps(
  limit = 1000,
  cursor = "",
  ascending = false,
  sortField = "ingestedAt" satisfies keyof Swap,
  poolId: string,
  traders?: string[],
  useSupabase = true, // Feature flag to control data source
): Promise<Swap[]> {
  // Use GraphQL if flag is disabled
  if (!useSupabase) {
    return getAllSwapsGraphQL(limit, cursor, ascending, sortField, poolId, traders)
  }

  try {
    // Parse cursor to get offset for pagination
    const offset = cursor ? Number.parseInt(cursor, 10) : 0

    // Build query - always order by created_at_ts_ms descending for most recent first
    let query = supabase
      .from("amm_transactions_with_reference")
      .select("*")
      .eq("amm_process", poolId)
      .order("created_at_ts_ms", { ascending: false })
      .range(offset, offset + limit - 1)

    // Filter by traders if provided
    if (traders && traders.length > 0) {
      query = query.in("user_id", traders)
    }

    const { data, error } = await query

    if (error) {
      console.error("Supabase query error:", error)
      throw new Error(`Failed to fetch swaps: ${error.message}`)
    }

    if (!data) {
      return []
    }

    // Transform Supabase data to match Swap type
    const swaps: Swap[] = data.map((tx: AmmTransaction, index: number) => {
      // Calculate reserves as numbers
      const reservesA = tx.reserves_token_a ? Number.parseFloat(tx.reserves_token_a) : 0
      const reservesB = tx.reserves_token_b ? Number.parseFloat(tx.reserves_token_b) : 0

      // Build tags object for backward compatibility
      const tags: Record<string, string> = {
        "Block-Height": tx.block_height?.toString() || "",
        Reference: tx.order_id || "",
        "Reserves-Token-A": tx.reserves_token_a || "0",
        "Reserves-Token-B": tx.reserves_token_b || "0",
        "Token-A": tx.token_a || "",
        "Token-B": tx.token_b || "",
      }

      // Calculate total fee as string for backward compatibility
      const fee = tx.total_fee?.toString() || "0"

      return {
        cursor: (offset + index + 1).toString(),
        id: tx.id,
        fromToken: tx.from_token || "",
        toToken: tx.to_token || "",
        fee,
        fromQuantity: tx.from_quantity || 0,
        toQuantity: tx.to_quantity || 0,
        tags,
        ingestedAt: tx.created_at_ts_ms || Date.now(),
        from: tx.user_id || tx.sender || "",
        reservesA,
        reservesB,
        ref: Number(tx.reference_value) || 0,
        tokenAPrice: tx.token_a_usd_price || undefined,
        tokenBPrice: tx.token_b_usd_price || undefined,
        tokenA: tx.token_a || undefined,
        tokenB: tx.token_b || undefined,
      }
    })

    return swaps
  } catch (error) {
    console.error("Error fetching swaps from Supabase:", error)
    // Fallback to GraphQL implementation if Supabase fails
    return getAllSwapsGraphQL(limit, cursor, ascending, sortField, poolId, traders)
  }
}

// Original GraphQL-based implementation (renamed)
async function getAllSwapsGraphQL(
  limit = 1000,
  cursor = "",
  ascending = false,
  sortField = "ingestedAt" satisfies keyof Swap,
  poolId: string,
  traders?: string[],
): Promise<Swap[]> {
  const result = await goldsky
    .query<TransactionsResponse>(AllSwapsQuery, {
      poolId,
      limit,
      sortOrder: ascending ? "HEIGHT_ASC" : "INGESTED_AT_DESC",
      cursor,
      traders,
    })
    .toPromise()
  const { data } = result

  if (!data) throw new Error("No data returned from query")

  const swaps = data.transactions.edges.map((edge: any) => {
    const cursor = edge.cursor
    const node = edge.node
    const tags = node.tags.reduce((acc: Record<string, string>, tag: any) => {
      acc[tag.name] = tag.value
      return acc
    }, {})

    const fromToken = tags["From-Token"]
    const toToken = tags["To-Token"]
    const fee = tags["Fee"]
    const fromQuantity = parseFloat(tags["From-Quantity"])
    const toQuantity = parseFloat(tags["To-Quantity"])
    const reservesA = parseFloat(tags["Reserves-Token-A"])
    const reservesB = parseFloat(tags["Reserves-Token-B"])
    // const from = tags["Pushed-For"]
    const from = tags["Relay-To"]

    delete tags["From-Token"]
    delete tags["To-Token"]
    delete tags["Fee"]
    delete tags["From-Quantity"]
    delete tags["To-Quantity"]
    // delete tags["Pushed-For"]

    const ingestedAt = node.ingested_at * 1000

    return {
      cursor,
      id: node.id,
      fromToken,
      toToken,
      fee,
      fromQuantity,
      toQuantity,
      tags,
      ingestedAt,
      from,
      reservesA,
      reservesB,
      ref: Number(tags["Reference"]),
    } satisfies Swap
  })

  return swaps
}
